import { useCallback, type ComponentType } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Handle,
  Position,
  type Node,
  type Edge,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Workflow, Shield, GitBranch, Database, Code2, FileJson,
  Play, Globe, Inbox,
} from 'lucide-react';
import { useT } from '@/i18n/provider';
import { cn } from '@/lib/utils';

/* ---- Node type config (matches apps/web PipelineCustomNode styling) ---- */

interface PipelineNodeConfig {
  icon: ComponentType<{ className?: string }>;
  bg: string;
  border: string;
  iconColor: string;
  typeLabel: string;
}

const nodeConfig: Record<string, PipelineNodeConfig> = {
  start: { icon: Play, bg: 'bg-emerald-500/20', border: 'border-emerald-500/50', iconColor: 'text-emerald-400', typeLabel: 'TRIGGER' },
  httpRequest: { icon: Globe, bg: 'bg-blue-500/10', border: 'border-blue-500/30', iconColor: 'text-blue-400', typeLabel: 'EXTRACT' },
  json: { icon: FileJson, bg: 'bg-blue-500/10', border: 'border-blue-500/30', iconColor: 'text-blue-400', typeLabel: 'TRANSFORM' },
  redactMask: { icon: Shield, bg: 'bg-rose-500/10', border: 'border-rose-500/30', iconColor: 'text-rose-400', typeLabel: 'TRANSFORM' },
  condition: { icon: GitBranch, bg: 'bg-amber-500/10', border: 'border-amber-500/30', iconColor: 'text-amber-400', typeLabel: 'FLOW' },
  lookup: { icon: Database, bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', iconColor: 'text-cyan-400', typeLabel: 'TRANSFORM' },
  transform: { icon: Code2, bg: 'bg-violet-500/10', border: 'border-violet-500/30', iconColor: 'text-violet-400', typeLabel: 'TRANSFORM' },
  logpulseIngest: { icon: Inbox, bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', iconColor: 'text-cyan-400', typeLabel: 'LOAD' },
};

/* ---- Custom ReactFlow node ---- */

function PipelineMockNode({ data }: NodeProps) {
  const config = nodeConfig[(data as Record<string, string>).nodeType] || nodeConfig.transform;
  const Icon = config.icon;
  const nodeType = (data as Record<string, string>).nodeType;
  const isCondition = nodeType === 'condition';
  const isStart = nodeType === 'start';

  return (
    <div className={cn(
      'rounded-xl border-2 min-w-[160px] bg-gray-950',
      config.border
    )}>
      {/* Input handle */}
      {!isStart && (
        <Handle
          type="target"
          position={Position.Left}
          className="!w-3 !h-3 !border-2 !bg-gray-800 !border-gray-500"
        />
      )}

      {/* Header */}
      <div className={cn('px-3 py-1.5 border-b border-gray-800/50 flex items-center gap-2', config.bg)}>
        <Icon className={cn('h-3.5 w-3.5', config.iconColor)} />
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          {config.typeLabel}
        </span>
      </div>

      {/* Body */}
      <div className={cn('px-3 py-2.5', isCondition ? 'min-h-[72px]' : 'min-h-[40px]')}>
        <span className="text-xs font-medium text-gray-100">
          {(data as Record<string, string>).label}
        </span>

        {/* Condition branch labels */}
        {isCondition && (
          <div className="mt-2 space-y-1">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#22c55e' }} />
              <span className="text-[10px] text-gray-400">level = error</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#71717a' }} />
              <span className="text-[10px] text-gray-400">else</span>
            </div>
          </div>
        )}
      </div>

      {/* Output handles */}
      {isCondition ? (
        <>
          <Handle
            type="source"
            position={Position.Right}
            id="true"
            className="!w-3 !h-3 !border-2 !bg-emerald-400/50 !border-emerald-400"
            style={{ top: '45%' }}
          />
          <Handle
            type="source"
            position={Position.Right}
            id="else"
            className="!w-3 !h-3 !border-2 !bg-gray-500/50 !border-gray-500"
            style={{ top: '75%' }}
          />
        </>
      ) : (
        <Handle
          type="source"
          position={Position.Right}
          className="!w-3 !h-3 !border-2 !bg-gray-800 !border-gray-500"
        />
      )}
    </div>
  );
}

const nodeTypes = { pipelineNode: PipelineMockNode };

/* ---- Flow data ---- */

const nodes: Node[] = [
  { id: 'start', type: 'pipelineNode', position: { x: 0, y: 140 }, data: { nodeType: 'start', label: 'Webhook Trigger' } },
  { id: 'http', type: 'pipelineNode', position: { x: 240, y: 140 }, data: { nodeType: 'httpRequest', label: 'Fetch Metadata' } },
  { id: 'json', type: 'pipelineNode', position: { x: 480, y: 140 }, data: { nodeType: 'json', label: 'Parse JSON' } },
  { id: 'redact', type: 'pipelineNode', position: { x: 720, y: 140 }, data: { nodeType: 'redactMask', label: 'Redact PII' } },
  { id: 'condition', type: 'pipelineNode', position: { x: 960, y: 120 }, data: { nodeType: 'condition', label: 'Check Level' } },
  { id: 'lookup', type: 'pipelineNode', position: { x: 1220, y: 60 }, data: { nodeType: 'lookup', label: 'Enrich from CMDB' } },
  { id: 'transform', type: 'pipelineNode', position: { x: 1220, y: 230 }, data: { nodeType: 'transform', label: 'Normalize Fields' } },
  { id: 'ingest1', type: 'pipelineNode', position: { x: 1480, y: 60 }, data: { nodeType: 'logpulseIngest', label: 'Ingest → Alerts' } },
  { id: 'ingest2', type: 'pipelineNode', position: { x: 1480, y: 230 }, data: { nodeType: 'logpulseIngest', label: 'Ingest → Logs' } },
];

const edges: Edge[] = [
  { id: 'e1', source: 'start', target: 'http', type: 'bezier', style: { stroke: '#344054', strokeWidth: 2 } },
  { id: 'e2', source: 'http', target: 'json', type: 'bezier', style: { stroke: '#344054', strokeWidth: 2 } },
  { id: 'e3', source: 'json', target: 'redact', type: 'bezier', style: { stroke: '#344054', strokeWidth: 2 } },
  { id: 'e4', source: 'redact', target: 'condition', type: 'bezier', style: { stroke: '#344054', strokeWidth: 2 } },
  { id: 'e5', source: 'condition', sourceHandle: 'true', target: 'lookup', type: 'bezier', style: { stroke: '#22c55e', strokeWidth: 2 } },
  { id: 'e6', source: 'condition', sourceHandle: 'else', target: 'transform', type: 'bezier', style: { stroke: '#71717a', strokeWidth: 2 } },
  { id: 'e7', source: 'lookup', target: 'ingest1', type: 'bezier', style: { stroke: '#344054', strokeWidth: 2 } },
  { id: 'e8', source: 'transform', target: 'ingest2', type: 'bezier', style: { stroke: '#344054', strokeWidth: 2 } },
];

/* ---- Capability cards ---- */

const nodeCategories = [
  { key: 'extract', icon: Database, color: 'text-info-500', bg: 'bg-info-50 dark:bg-info-500/10' },
  { key: 'transform', icon: Code2, color: 'text-primary-500', bg: 'bg-primary-50 dark:bg-primary-500/10' },
  { key: 'flowControl', icon: GitBranch, color: 'text-warning-500', bg: 'bg-warning-50 dark:bg-warning-500/10' },
  { key: 'compliance', icon: Shield, color: 'text-success-500', bg: 'bg-success-50 dark:bg-success-500/10' },
] as const;

/* ---- Section ---- */

export function PipelineSection() {
  const t = useT();

  // Prevent interactions — display only
  const noop = useCallback(() => {}, []);

  return (
    <section id="pipelines" className="py-24 sm:py-32 bg-white dark:bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-2xl mx-auto text-center mb-16 sm:mb-20 animate-on-scroll">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800/30 mb-4">
            <Workflow className="h-3.5 w-3.5 text-primary-500" />
            <span className="text-xs font-medium text-primary-700 dark:text-primary-300">
              {t('pipelineSection.badge')}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white text-balance">
            {t('pipelineSection.title')}
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 text-balance">
            {t('pipelineSection.subtitle')}
          </p>
        </div>

        {/* Pipeline editor mock — full width */}
        <div className="animate-on-scroll mb-16">
          <div className="rounded-2xl border border-gray-200/60 dark:border-gray-700/40 bg-white dark:bg-gray-950 shadow-xl overflow-hidden">
            {/* Editor header bar */}
            <div className="flex items-center gap-2.5 px-4 py-3 border-b border-gray-200/60 dark:border-gray-700/40 bg-gray-50 dark:bg-gray-900">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400/60" />
                <div className="w-3 h-3 rounded-full bg-amber-400/60" />
                <div className="w-3 h-3 rounded-full bg-emerald-400/60" />
              </div>
              <div className="ml-2 flex items-center gap-2">
                <Workflow className="h-4 w-4 text-primary-500" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">{t('pipelineSection.editorTitle')}</span>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {t('pipelineSection.editorActive')}
                </span>
              </div>
            </div>

            {/* ReactFlow canvas */}
            <div className="h-[340px] bg-gray-950">
              <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                fitView
                fitViewOptions={{ maxZoom: 0.75, padding: 0.15 }}
                nodesDraggable={false}
                nodesConnectable={false}
                elementsSelectable={false}
                panOnDrag={false}
                zoomOnScroll={false}
                zoomOnPinch={false}
                zoomOnDoubleClick={false}
                preventScrolling={false}
                onNodeClick={noop}
                proOptions={{ hideAttribution: true }}
              >
                <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#1D2939" />
              </ReactFlow>
            </div>
          </div>
        </div>

        {/* Capability cards + trigger modes */}
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 mb-10">
          {nodeCategories.map((cat, i) => {
            const Icon = cat.icon;
            return (
              <div
                key={cat.key}
                className="animate-on-scroll flex items-start gap-4 p-5 rounded-xl glass-subtle card-hover"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center shrink-0', cat.bg)}>
                  <Icon className={cn('h-5 w-5', cat.color)} />
                </div>
                <div>
                  <p className="font-semibold text-sm text-gray-900 dark:text-white">
                    {t(`pipelineSection.cat.${cat.key}.title`)}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {t(`pipelineSection.cat.${cat.key}.description`)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Trigger modes */}
        <div className="animate-on-scroll max-w-md mx-auto">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 text-center">
            {t('pipelineSection.triggerTitle')}
          </p>
          <div className="flex gap-2">
            {(['schedule', 'webhook', 'manual'] as const).map((mode) => (
              <div
                key={mode}
                className={cn(
                  'flex-1 text-center px-3 py-2 rounded-lg border text-xs font-medium',
                  mode === 'webhook'
                    ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'
                )}
              >
                {t(`pipelineSection.trigger.${mode}`)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
